// What only a saltwater tank uses. Feedback #74 (2026-10-13): a keeper who
// keyed in a freshwater tank was still offered a refractometer, a reef fact
// a day and a saltwater species list. ICP stays: a freshwater ICP is read
// for contaminants (icpTankContext in server/icp-analysis.js).

import { isFreshwaterTank } from './first-run.js';

// A refractometer reads salinity.
const SALTWATER_LOG_METHODS = new Set(['refract']);

export function logMethodFits(tank, method) {
  return !isFreshwaterTank(tank) || !SALTWATER_LOG_METHODS.has(method);
}

// The fact pool (server/db/seeds/reef-facts.json) holds reef facts only.
export function showsReefFact(tank) {
  return !isFreshwaterTank(tank);
}

// The species catalog is saltwater only (livestock-saltwater.json).
export function catalogFits(tank) {
  return !isFreshwaterTank(tank);
}

// The species list's empty state. On a freshwater tank nothing in the
// saltwater catalog fits, and "turn off Fits my tank to see more" only led
// to saltwater species, so it says so and points at adding by name.
export function catalogEmptyMessage(tank, { phase, fitsOnly }) {
  const fresh = !catalogFits(tank);
  if (phase === 'cycling') {
    return 'Your tank is still cycling. Sensei recommends waiting until ammonia and nitrite read zero before adding anything alive.';
  }
  if (phase === 'setup') {
    return fresh
      ? 'Tank is being set up. Let it cycle before adding anything alive.'
      : 'Tank is being set up. Stabilise salinity and temperature before stocking.';
  }
  if (fresh) {
    return 'The species list is saltwater only for now, so nothing in it fits a freshwater tank. Add your fish and shrimp by name.';
  }
  if (fitsOnly) {
    return 'No species fit this tank with the current filters. Turn off "Fits my tank" to see more options and the reasons.';
  }
  return 'No species match. Try clearing the filters or the search.';
}

// The custom species sheet's hint (feedback #73, 2026-10-15: "Can we not use
// Google to populate this fish stuff"). It said "Admin will use this to
// expand the catalog", but no one saw a name typed without a shared photo,
// and nothing reads the details beyond its notes. The details are optional,
// and only the saltwater list grows from the names.
export function customSpeciesHint(tank) {
  const base =
    "The details below are optional: leave blank anything you don't know. They are kept in its notes.";
  return catalogFits(tank)
    ? `Not in the list? Add it by name. ${base} We see the names keepers add and use them to grow the list.`
    : `Add it by name. ${base}`;
}

// "Reef-safe" means nothing on a freshwater tank, and neither do stars,
// urchins or anemones (feedback #74).
const FRESH_INVERT_TYPES = ['snail', 'shrimp', 'crayfish', 'crab', 'clam', 'other'];

export function customSpeciesFields(fields, tank) {
  if (catalogFits(tank)) return fields;
  return fields
    .filter((f) => f.key !== 'reef_safe')
    .map((f) =>
      f.key === 'category' && f.options?.includes('snail')
        ? { ...f, options: FRESH_INVERT_TYPES }
        : f
    );
}
